import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import RequestDialog from "@/components/RequestDialog";
import { ArrowLeft, ArrowRight, ArrowUpRight, FileText, Loader2, Sparkles, Play } from "lucide-react";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProject(null);
    setNotFound(false);
    api
      .get(`/projects/${slug}`)
      .then((res) => setProject(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="project-not-found">
        <h1 className="font-display text-4xl font-black uppercase mb-4">Project not found</h1>
        <Link to="/" className="text-[#E8954A] flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to slate
        </Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div data-testid="project-detail-page">
      {/* Cinematic header */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={project.banner || project.poster} alt={project.title} className={`absolute inset-0 w-full h-full object-cover ${project.banner ? "object-top" : ""}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-14">
          <button
            onClick={() => navigate("/#work")}
            data-testid="back-to-slate"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to the slate
          </button>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span
              className="text-[11px] tracking-[0.15em] uppercase font-bold px-3 py-1 rounded-sm border"
              style={{ color: project.accent, borderColor: `${project.accent}66` }}
            >
              {project.status}
            </span>
            <span className="text-white/55 text-sm">{project.format}</span>
            <span className="text-white/30">•</span>
            <span className="text-white/55 text-sm">{project.genre}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tighter leading-none max-w-4xl">
            {project.title}
          </h1>
          <p className="text-white/80 text-xl md:text-2xl mt-5 max-w-2xl italic">"{project.tagline}"</p>
        </div>
      </section>

      {/* Trailer */}
      {project.trailer && (
        <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-16 -mb-4" data-testid="project-trailer">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-4">Official Trailer</p>
          <div className="relative overflow-hidden rounded-sm border border-white/10 bg-black">
            {project.trailer.includes("player.vimeo.com") ? (
              <iframe
                src={project.trailer}
                title={`${project.title} trailer`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full aspect-video bg-black"
                data-testid="trailer-video"
              />
            ) : project.trailer.includes("vimeo.com") ? (
              <a
                href={project.trailer}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trailer-link"
                className="group relative block w-full aspect-video overflow-hidden"
              >
                <img src={project.poster} alt={`${project.title} trailer`} className="absolute inset-0 w-full h-full object-cover opacity-55 transition-all duration-500 group-hover:opacity-70 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/50" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                  <span className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110">
                    <Play className="w-8 h-8 text-black ml-1" fill="currentColor" />
                  </span>
                  <span className="text-white font-semibold text-lg tracking-wide flex items-center gap-2">
                    Watch the Trailer on Vimeo <ArrowUpRight className="w-5 h-5" />
                  </span>
                </div>
              </a>
            ) : (
              <video
                src={project.trailer}
                controls
                playsInline
                poster={project.poster}
                className="w-full aspect-video object-contain bg-black"
                data-testid="trailer-video"
              />
            )}
          </div>
        </section>
      )}

      {/* Body */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 grid lg:grid-cols-3 gap-14">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-3">Logline</p>
            <p className="text-2xl md:text-3xl font-display font-medium leading-snug tracking-tight">{project.logline}</p>
          </div>
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-3">Synopsis</p>
            <p className="text-white/70 text-lg leading-relaxed">{project.synopsis}</p>
          </div>

          {project.world && (
            <div data-testid="project-world">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-3">The World</p>
              <p className="text-white/70 text-lg leading-relaxed">{project.world}</p>
            </div>
          )}

          {project.gallery?.length > 0 && (
            <div data-testid="project-gallery">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-4">Concept Stills</p>
              <div className="grid gap-4">
                {project.gallery.map((src, i) => (
                  <div key={src} className="relative overflow-hidden rounded-sm border border-white/10 group">
                    <img
                      src={src}
                      alt={`${project.title} concept ${i + 1}`}
                      className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {project.highlights?.length > 0 && (
            <div data-testid="project-highlights">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-4">Why It Sells</p>
              <ul className="space-y-3">
                {project.highlights.map((h) => (
                  <li key={h} className="flex gap-3 text-white/75 leading-relaxed">
                    <Sparkles className="w-5 h-5 shrink-0 mt-0.5" style={{ color: project.accent }} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {project.characters?.length > 0 && (
            <div data-testid="project-characters">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-4">Characters</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {project.characters.map((c, i) => (
                  <div key={i} className="border border-white/10 rounded-sm bg-[#141414] p-5">
                    <p className="font-display text-lg font-bold tracking-tight">{c.name}</p>
                    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: project.accent }}>{c.role}</p>
                    <p className="text-white/60 text-sm leading-relaxed">{c.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {project.beats?.length > 0 && (
            <div data-testid="project-beats">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-4">Story Beats</p>
              <div className="space-y-0">
                {project.beats.map((b, i) => (
                  <div key={b.title} className="flex gap-5 pb-6 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span className="font-display text-sm font-bold w-8 h-8 rounded-full border border-white/15 flex items-center justify-center" style={{ color: project.accent }}>
                        {i + 1}
                      </span>
                      {i < project.beats.length - 1 && <span className="w-px flex-1 bg-white/10 mt-2" />}
                    </div>
                    <div className="pb-2">
                      <p className="font-display text-lg font-bold tracking-tight">{b.title}</p>
                      <p className="text-white/60 text-sm leading-relaxed mt-1">{b.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {project.producers && (
            <div data-testid="project-producers">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-3">Producers</p>
              <p className="text-white/80 text-lg">{project.producers}</p>
            </div>
          )}
        </div>

        {/* Sticky request card */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 border border-white/10 rounded-sm bg-[#141414] p-8">
            <FileText className="w-8 h-8 text-[#E8954A] mb-5" strokeWidth={1.6} />
            <h3 className="font-display text-2xl font-bold tracking-tight mb-2">Read the full story</h3>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Request the complete screenplay or full pitch deck for <span className="text-white">{project.title}</span>. Available to verified industry professionals.
            </p>
            <div className="space-y-3 mb-6 text-sm">
              {["Full screenplay / treatment", "Detailed pitch deck", "Direct line to the creators"].map((f) => (
                <div key={f} className="flex items-center gap-3 text-white/70">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: project.accent }} />
                  {f}
                </div>
              ))}
            </div>
            <button
              onClick={() => setOpen(true)}
              data-testid="request-pitch-btn"
              className="w-full bg-white text-black font-semibold py-3.5 rounded-sm hover:bg-[#E8954A] transition-colors flex items-center justify-center gap-2"
            >
              Request Screenplay / Pitch <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </aside>
      </section>

      <RequestDialog open={open} onOpenChange={setOpen} project={project} />
    </div>
  );
}
